import { useState } from "react";
import { mockPaymentNotifications } from "@/mocks/dealerData";

type FilterStatus = "all" | "pending" | "completed" | "rejected";

export default function PaymentNotificationsPage() {
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [search, setSearch] = useState("");

  const notifications = mockPaymentNotifications;

  const filtered = notifications
    .filter((n) => (filter === "all" ? true : n.status === filter))
    .filter(
      (n) =>
        n.note.toLowerCase().includes(search.toLowerCase()) ||
        n.ibanName.toLowerCase().includes(search.toLowerCase()) ||
        n.ibanNumber.includes(search)
    );

  const pendingCount = notifications.filter((n) => n.status === "pending").length;

  const statusLabel = (status: string) => {
    switch (status) {
      case "completed": return { label: "Onaylandı", classes: "bg-primary-100 text-primary-700" };
      case "rejected": return { label: "Reddedildi", classes: "bg-foreground-100 text-foreground-500" };
      default: return { label: "Beklemede", classes: "bg-accent-100 text-accent-700" };
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">
            <a id="payment-notifications-title">Ödeme Bildirimleri</a>
          </h4>
          <p className="text-sm text-foreground-500 mt-0.5">
            Gönderdiğiniz ödeme bildirimlerinin durumunu takip edin.
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-accent-600 font-medium">
                <i className="ri-time-line"></i>
                {pendingCount} onay bekliyor
              </span>
            )}
          </p>
        </div>
        <div className="relative w-full sm:w-56">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Bildirimlerde ara..."
            className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
        {[
          { key: "all", label: "Tümü" },
          { key: "pending", label: `Beklemede ${pendingCount > 0 ? `(${pendingCount})` : ""}` },
          { key: "completed", label: "Onaylanan" },
          { key: "rejected", label: "Reddedilen" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as FilterStatus)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === tab.key
                ? "bg-background-50 text-foreground-950"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[650px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Banka</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">IBAN</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Not</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-foreground-400">
                    <i className="ri-notification-off-line text-2xl block mb-2"></i>
                    {filter === "pending"
                      ? "Bekleyen bildiriminiz bulunmuyor."
                      : "Kayıt bulunmuyor."}
                  </td>
                </tr>
              ) : (
                filtered.map((n) => {
                  const st = statusLabel(n.status);
                  return (
                    <tr
                      key={n.id}
                      className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">
                        {new Date(n.createdAt).toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                        <br />
                        <span className="text-foreground-400">
                          {new Date(n.createdAt).toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 font-medium">
                        {n.ibanName}
                      </td>
                      <td className="px-4 py-3 text-foreground-500 text-xs font-mono">
                        {n.ibanNumber}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-900">
                        ${n.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-foreground-500 text-xs max-w-[200px] truncate">
                        {n.note || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.classes}`}
                        >
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-background-200/70">
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-foreground-400">
              <i className="ri-notification-off-line text-2xl block mb-2"></i>
              {filter === "pending" ? "Bekleyen bildiriminiz bulunmuyor." : "Kayıt bulunmuyor."}
            </div>
          ) : (
            filtered.map((n) => {
              const st = statusLabel(n.status);
              return (
                <div key={n.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.classes}`}>{st.label}</span>
                        <span className="text-xs text-foreground-400">
                          {new Date(n.createdAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground-900 mt-1">{n.ibanName}</p>
                      <p className="text-xs text-foreground-500 mt-0.5 font-mono">{n.ibanNumber}</p>
                      {n.note && <p className="text-xs text-foreground-500 mt-1">{n.note}</p>}
                    </div>
                    <p className="text-sm font-bold text-foreground-950 shrink-0">${n.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center shrink-0">
            <i className="ri-time-line text-accent-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-foreground-400">Bekleyen</p>
            <p className="text-lg font-semibold text-foreground-950">{pendingCount} bildirim</p>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
            <i className="ri-check-line text-primary-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-foreground-400">Onaylanan</p>
            <p className="text-lg font-semibold text-foreground-950">
              {notifications.filter((n) => n.status === "completed").length} bildirim
            </p>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-foreground-100 rounded-lg flex items-center justify-center shrink-0">
            <i className="ri-close-line text-foreground-500 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-foreground-400">Reddedilen</p>
            <p className="text-lg font-semibold text-foreground-950">
              {notifications.filter((n) => n.status === "rejected").length} bildirim
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}